import React from 'react';
import {
  ArrowRight,
  AlertCircle,
  Calendar,
  Check,
  CheckSquare,
  ClipboardList,
  History,
  Package,
  Plus,
  RefreshCw,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  ManualOrderItemDraft,
  Order,
  OrderHistory as IOrderHistory,
  OrderStatus,
} from '../../types';
import { getOrderHistories } from '../../api/history';
import { updateOrder } from '../../api/orders';
import { formatPhoneNumber } from '../../utils/formatters';
import {
  getOrderItemAggregationKey,
  getOrderItemBaseName,
  getOrderItemOptionLabel,
} from '../../utils/orderItemDisplay';
import { getSellerCustomerHistoryDetail } from '../../api/seller-page-details';

interface CustomerOrderHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerName: string;
  customerPhone: string;
  userId?: string;
  orderSheetId?: string;
  onUpdateOrderStatus?: (order: Order) => Promise<void> | void;
  onUpdateOrderStatusBatch?: (
    ids: string[],
    status: OrderStatus,
    modifierRole?: string,
    partialUpdates?: { id: string; itemIds: string[] }[],
  ) => Promise<void>;
  onOpenAdminOrder?: (order: Order) => void;
  onUpdateAlias?: (phone: string, nick: string) => Promise<void>;
  onOrderMutated?: (order: Order) => void;
}

const ReceiptConfirmationPopup: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedItems: any[];
  totalAmount: number;
}> = ({ isOpen, onClose, onConfirm, selectedItems, totalAmount }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[8000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-sm bg-white rounded-[32px] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in duration-200">
        <div className="p-6 border-b flex justify-between items-center bg-gray-50/50">
          <h4 className="text-lg font-black text-gray-900">픽업 수령 처리</h4>
          <button
            onClick={onClose}
            className="p-2 bg-white rounded-full text-gray-400 border shadow-sm"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4 no-scrollbar">
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 flex gap-3">
            <AlertCircle className="text-blue-500 shrink-0" size={18} />
            <p className="text-[11px] font-bold text-blue-700 leading-relaxed">
              선택하신 상품들을 '입금/수령완료' 상태로 변경합니다.
              <br />
              확인 시 해당 수량만큼 재고가 차감됩니다.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter ml-1">
              선택 품목
            </p>
            {selectedItems.map((item, idx) => {
              const optionLabel = getOrderItemOptionLabel(item);
              const imageUrl = sanitizeImageUrl(item.image);

              return (
                <div
                  key={idx}
                  className="flex justify-between items-center gap-3 bg-gray-50 p-3 rounded-2xl border border-gray-100"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={getOrderItemBaseName(item)}
                      className="w-10 h-10 rounded-xl object-cover border bg-white shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl border bg-white shrink-0 flex items-center justify-center text-gray-300">
                      <Package size={14} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black truncate">{getOrderItemBaseName(item)}</p>
                    {optionLabel && (
                      <p className="text-[10px] font-black text-emerald-600 mt-0.5 truncate">
                        옵션: {optionLabel}
                      </p>
                    )}
                    <p className="text-[10px] font-bold text-gray-400 mt-0.5">
                      {item.quantity}개 / {item.price.toLocaleString()}원
                    </p>
                  </div>
                  <div className="text-xs font-black text-gray-900 shrink-0">
                    {(item.price * item.quantity).toLocaleString()}원
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t space-y-3">
          <div className="flex justify-between items-center px-1">
            <span className="text-xs font-black text-gray-400">최종 합계 금액</span>
            <span className="text-xl font-black text-emerald-600">
              {totalAmount.toLocaleString()}원
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 h-12 bg-white border border-gray-200 text-gray-400 rounded-2xl font-black text-xs hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 h-12 bg-gray-900 text-white rounded-2xl font-black text-xs shadow-lg hover:bg-gray-800 transition-all active:scale-95"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const sanitizeImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const isCompletableStatus = (status?: string) =>
  status === 'PENDING' || status === 'READY';

const ITEM_STATUS_META: Record<string, { label: string; className: string }> = {
  PENDING: {
    label: '주문완료',
    className: 'bg-blue-50 text-blue-600',
  },
  READY: {
    label: '상품준비완료',
    className: 'bg-amber-50 text-amber-600',
  },
  COMPLETED: {
    label: '수령완료',
    className: 'bg-emerald-50 text-emerald-600',
  },
  CANCELLED: {
    label: '취소됨',
    className: 'bg-red-50 text-red-500',
  },
  PARTIAL: {
    label: '부분 처리',
    className: 'bg-slate-100 text-slate-600',
  },
};

const getStatusMeta = (status?: string) =>
  ITEM_STATUS_META[status || ''] || {
    label: status || '상태없음',
    className: 'bg-gray-100 text-gray-500',
  };

const HISTORY_STATUS_META: Record<
  string,
  { label: string; className: string; dotClassName: string }
> = {
  PENDING: {
    label: '주문완료',
    className: 'bg-blue-50 text-blue-600 border border-blue-100',
    dotClassName: 'bg-blue-400',
  },
  READY: {
    label: '상품준비완료',
    className: 'bg-amber-50 text-amber-600 border border-amber-200',
    dotClassName: 'bg-amber-400',
  },
  COMPLETED: {
    label: '입금/수령완료',
    className: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    dotClassName: 'bg-emerald-500',
  },
  CANCELLED: {
    label: '취소됨',
    className: 'bg-red-50 text-red-500 border border-red-100',
    dotClassName: 'bg-red-400',
  },
  PARTIAL: {
    label: '부분 처리',
    className: 'bg-slate-100 text-slate-600 border border-slate-200',
    dotClassName: 'bg-slate-400',
  },
};

const HistoryStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = HISTORY_STATUS_META[status] || {
    label: status,
    className: 'bg-gray-100 text-gray-500 border border-gray-200',
    dotClassName: 'bg-gray-400',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-black px-2 py-1 rounded-full ${config.className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dotClassName}`} />
      {config.label}
    </span>
  );
};

const ACTION_LABEL: Record<string, string> = {
  UPDATE_ITEMS: '품목 수정',
  UPDATE_PRICE: '상품 가격 변경',
  UPDATE_STATUS: '상태 변경',
  UPDATE_INFO: '정보 변경',
  CREATE: '주문 생성',
  CANCEL: '주문 취소',
  BATCH_UPDATE: '일괄 상태 변경',
  PARTIAL_SPLIT_COMPLETE: '일부 품목 상태 변경',
  STOCK_RESTORED_BY_ORDER_CANCEL: '재고 복구 (취소)',
  STOCK_DEDUCTED_BY_ORDER: '재고 차감',
};

const describeItemChange = (change: {
  name: string;
  oldQty: number;
  newQty: number;
  diff: number;
  changeKind?: string;
}) => {
  const label = change.name;

  if (change.changeKind === 'STATUS_ONLY' || change.diff === 0) {
    return {
      text: `${label} ${change.newQty}개 상태 변경`,
      type: 'status' as const,
    };
  }

  if (change.newQty === 0) {
    return {
      text: `${label} 취소`,
      type: 'remove' as const,
    };
  }

  if (change.oldQty === 0) {
    return {
      text: `${label} ${change.newQty}개 추가`,
      type: 'add' as const,
    };
  }

  return {
    text: `${label} ${change.oldQty}개 → ${change.newQty}개`,
    type: change.diff > 0 ? ('increase' as const) : ('decrease' as const),
  };
};

const ItemChangeLine: React.FC<{ change: any }> = ({ change }) => {
  const { text, type } = describeItemChange(change);
  const iconProps = { size: 10, strokeWidth: 3 };

  return (
    <div className="flex items-center gap-2 py-0.5">
      <span
        className={`shrink-0 w-4 h-4 flex items-center justify-center rounded-full ${
          type === 'remove'
            ? 'bg-red-100 text-red-500'
            : type === 'add'
            ? 'bg-green-100 text-green-600'
            : type === 'status'
            ? 'bg-slate-100 text-slate-500'
            : type === 'increase'
            ? 'bg-blue-100 text-blue-500'
            : 'bg-orange-100 text-orange-500'
        }`}
      >
        {type === 'remove' ? (
          <Trash2 {...iconProps} />
        ) : type === 'add' ? (
          <Plus {...iconProps} />
        ) : (
          <RefreshCw {...iconProps} />
        )}
      </span>
      <span
        className={`text-[11px] font-bold ${
          type === 'remove'
            ? 'text-red-500 line-through'
            : type === 'add'
            ? 'text-green-600'
            : type === 'status'
            ? 'text-slate-500'
            : type === 'increase'
            ? 'text-blue-600'
            : 'text-orange-500'
        }`}
      >
        {text}
      </span>
    </div>
  );
};

const OrderEditTimeline: React.FC<{ logs: IOrderHistory[] }> = ({ logs }) => {
  if (logs.length === 0) {
    return (
      <div className="mt-3 p-4 bg-gray-50 rounded-2xl text-center animate-in fade-in duration-200">
        <p className="text-[11px] text-gray-400">변경 이력이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200">
      <div className="relative">
        <div className="absolute left-[11px] top-3 bottom-3 w-px bg-gray-200" />

        <div className="space-y-4">
          {logs.map((log, logIdx) => {
            const isStatusChange = log.oldStatus !== log.newStatus && !!log.oldStatus;
            const hasItemChanges = (log.details?.itemChanges?.length || 0) > 0;
            const priceChanges = Array.isArray(log.details?.priceChanges)
              ? log.details.priceChanges
              : [];
            const hasPriceChange =
              log.details?.newPrice !== undefined
              && log.details?.oldPrice !== undefined;
            const isByBuyer =
              log.modifierRole === 'CUSTOMER' || log.modifierRole === 'USER';
            const isFirst = logIdx === logs.length - 1;

            return (
              <div key={log.id} className="flex gap-3 relative">
                <div
                  className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center z-10 mt-0.5 ${
                    isFirst
                      ? 'bg-emerald-500 text-white'
                      : isStatusChange
                      ? 'bg-amber-100 border-2 border-amber-400'
                      : 'bg-white border-2 border-gray-300'
                  }`}
                >
                  {isFirst ? (
                    <Package size={11} strokeWidth={2.5} />
                  ) : isStatusChange ? (
                    <ArrowRight
                      size={10}
                      className="text-amber-500"
                      strokeWidth={3}
                    />
                  ) : (
                    <RefreshCw
                      size={10}
                      className="text-gray-400"
                      strokeWidth={2.5}
                    />
                  )}
                </div>

                <div className="flex-1 pb-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`text-[10px] font-black px-1.5 py-0.5 rounded-md ${
                          isByBuyer
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {isByBuyer ? '본인' : '판매자'}
                      </span>
                      <span className="text-[11px] font-black text-gray-800">
                        {ACTION_LABEL[log.action || ''] || log.action || '변경'}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 shrink-0">
                      {format(new Date(log.createdAt), 'M.d HH:mm', {
                        locale: ko,
                      })}
                    </span>
                  </div>

                  {isStatusChange && (
                    <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                      {log.oldStatus && (
                        <HistoryStatusBadge status={log.oldStatus} />
                      )}
                      <ArrowRight size={12} className="text-gray-300" />
                      <HistoryStatusBadge status={log.newStatus} />
                    </div>
                  )}

                  {hasItemChanges && (
                    <div className="bg-white rounded-xl border border-gray-100 px-3 py-2 space-y-0.5 shadow-sm">
                      {log.details.itemChanges.map((change: any, idx: number) => (
                        <ItemChangeLine key={idx} change={change} />
                      ))}
                    </div>
                  )}

                  {priceChanges.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-100 px-3 py-2 space-y-1 shadow-sm">
                      {priceChanges.map((change: any, idx: number) => (
                        <div key={idx} className="text-[10px] font-bold text-gray-500">
                          <span className="text-gray-800">{change.name}</span>{' '}
                          {change.oldUnitPrice.toLocaleString()}원 →{' '}
                          <span className="text-emerald-700">
                            {change.newUnitPrice.toLocaleString()}원
                          </span>{' '}
                          · {change.quantity}개
                        </div>
                      ))}
                    </div>
                  )}

                  {hasPriceChange && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-gray-500 bg-white rounded-lg px-2.5 py-1.5 border border-gray-100 w-fit">
                      <span>{log.details.oldPrice.toLocaleString()}원</span>
                      <ArrowRight size={10} className="text-gray-300" />
                      <span className="text-gray-800">
                        {log.details.newPrice.toLocaleString()}원
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const getDisplayStatus = (statuses: string[]) => {
  const uniqueStatuses = Array.from(new Set(statuses));
  return uniqueStatuses.length === 1 ? uniqueStatuses[0] : 'PARTIAL';
};

export const CustomerOrderHistoryModal: React.FC<
  CustomerOrderHistoryModalProps
> = ({
  isOpen,
  onClose,
  customerName,
  customerPhone,
  userId,
  orderSheetId,
  onUpdateOrderStatus,
  onUpdateOrderStatusBatch,
  onOpenAdminOrder,
  onOrderMutated,
}) => {
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [selectedItems, setSelectedItems] = React.useState<Record<string, boolean>>(
    {},
  );
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [expandedHistory, setExpandedHistory] = React.useState<
    Record<string, IOrderHistory[]>
  >({});
  const [loadingHistory, setLoadingHistory] = React.useState<
    Record<string, boolean>
  >({});
  const [memoEditorGroup, setMemoEditorGroup] = React.useState<any | null>(null);
  const [memoDraft, setMemoDraft] = React.useState('');
  const [isSavingMemo, setIsSavingMemo] = React.useState(false);
  const [manualOrderGroup, setManualOrderGroup] = React.useState<any | null>(null);
  const [manualOrderDraft, setManualOrderDraft] =
    React.useState<ManualOrderItemDraft>({
      name: '',
      quantity: '1',
      price: '',
    });
  const [isSavingManualOrder, setIsSavingManualOrder] = React.useState(false);

  const getItemSelectionKey = React.useCallback((groupKey: string, item: any) => {
    return `${groupKey}-${getOrderItemAggregationKey(item)}-${item.status || 'PENDING'}`;
  }, []);

  const loadHistory = React.useCallback(async () => {
    if (!isOpen) {
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const nextOrders = await getSellerCustomerHistoryDetail({
        customerName,
        customerPhone,
        userId,
        orderSheetId,
      });
      setOrders(nextOrders);
      setSelectedItems({});
      setIsConfirmOpen(false);
      setExpandedHistory({});
      setLoadingHistory({});
    } catch (error) {
      console.error('Failed to load customer history:', error);
      setLoadError('주문 내역을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [customerName, customerPhone, isOpen, orderSheetId, userId]);

  React.useEffect(() => {
    if (!isOpen) {
      setOrders([]);
      setSelectedItems({});
      setIsConfirmOpen(false);
      setLoadError(null);
      setExpandedHistory({});
      setLoadingHistory({});
      setMemoEditorGroup(null);
      setMemoDraft('');
      setManualOrderGroup(null);
      setManualOrderDraft({ name: '', quantity: '1', price: '' });
      setIsSavingManualOrder(false);
      return;
    }

    void loadHistory();
  }, [isOpen, loadHistory]);

  const groupedOrders = React.useMemo(() => {
    const groupMap = new Map<string, any>();

    orders.forEach((order) => {
      const groupKey = order.orderSheetId || `NO_SHEET:${order.storeId}`;
      const entry = {
        ...order,
        items: (order.items || []).map((item) => ({
          ...item,
          orderId: order.id,
          status: order.status,
          sourceOrderIds: [order.id],
          sourceItemIds: item.id ? [item.id] : [],
          sourceEntries: item.id ? [{ orderId: order.id, itemId: item.id }] : [],
        })),
      };

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          groupKey,
          orderSheetId: order.orderSheetId,
          orderSheetLabel:
            order.orderSheet?.label || (order.orderSheetId ? '주문지' : '주문지 미지정'),
          deliveryDate: order.orderSheet?.deliveryDate,
          storeName: order.storeName,
          entries: [entry],
          aggregatedIds: [order.id],
          manageableIds: isCompletableStatus(order.status) ? [order.id] : [],
          request: order.request || '',
          sellerMemo: order.sellerMemo?.trim() || '',
          latestCreatedAt: order.createdAt || order.date,
        });
        return;
      }

      const group = groupMap.get(groupKey);
      group.entries.push(entry);

      if (!group.aggregatedIds.includes(order.id)) {
        group.aggregatedIds.push(order.id);
      }

      if (
        isCompletableStatus(order.status)
        && !group.manageableIds.includes(order.id)
      ) {
        group.manageableIds.push(order.id);
      }

      if (order.request && !group.request) {
        group.request = order.request;
      } else if (
        order.request
        && group.request
        && !group.request.includes(order.request)
      ) {
        group.request += `\n${order.request}`;
      }

      const sellerMemo = order.sellerMemo?.trim();
      if (sellerMemo && !group.sellerMemo) {
        group.sellerMemo = sellerMemo;
      } else if (
        sellerMemo
        && group.sellerMemo
        && !group.sellerMemo.includes(sellerMemo)
      ) {
        group.sellerMemo += `\n${sellerMemo}`;
      }

      const nextCreatedAt = new Date(order.createdAt || order.date).getTime();
      const currentCreatedAt = new Date(group.latestCreatedAt).getTime();
      if (nextCreatedAt > currentCreatedAt) {
        group.latestCreatedAt = order.createdAt || order.date;
      }
    });

    return Array.from(groupMap.values())
      .map((group) => {
        const entries = [...group.entries].sort(
          (a, b) =>
            new Date(b.createdAt || b.date).getTime()
            - new Date(a.createdAt || a.date).getTime(),
        );

        const itemMap = new Map<string, any>();
        entries.forEach((entry) => {
          entry.items.forEach((item: any) => {
            const itemKey = `${getOrderItemAggregationKey(item)}-${item.status}`;
            const sourceEntry = item.id
              ? { orderId: entry.id, itemId: item.id }
              : null;

            if (!itemMap.has(itemKey)) {
              itemMap.set(itemKey, {
                ...item,
                sourceOrderIds: [entry.id],
                sourceItemIds: item.id ? [item.id] : [],
                sourceEntries: sourceEntry ? [sourceEntry] : [],
              });
              return;
            }

            const target = itemMap.get(itemKey);
            target.quantity += item.quantity;

            if (!target.sourceOrderIds.includes(entry.id)) {
              target.sourceOrderIds.push(entry.id);
            }

            if (item.id && !target.sourceItemIds.includes(item.id)) {
              target.sourceItemIds.push(item.id);
            }

            if (
              sourceEntry
              && !target.sourceEntries.some(
                (entryRef: { orderId: string; itemId: string }) =>
                  entryRef.orderId === sourceEntry.orderId
                  && entryRef.itemId === sourceEntry.itemId,
              )
            ) {
              target.sourceEntries.push(sourceEntry);
            }
          });
        });

        const items = Array.from(itemMap.values());
        // 최근 주문 = 마지막 주문/수정 시각. createdAt은 첫 주문 시각이라, 품목 수정·추가·삭제 시
        // 갱신되는 lastOrderedAt(주문+품목 created_at 최대값) 기준으로 표시한다.
        // 판매자 상태변경(updatedAt)은 품목을 안 건드리므로 의도적으로 제외.
        const latestActivityAt = entries.reduce((max, entry) => {
          const t = new Date(entry.lastOrderedAt || entry.createdAt || entry.date).getTime();
          return Number.isFinite(t) && t > max ? t : max;
        }, 0);
        const latestActivityLabel = latestActivityAt
          ? new Date(latestActivityAt).toLocaleString('ko-KR')
          : (entries[0]?.date || '');
        const displayStatus = getDisplayStatus(entries.map((entry) => entry.status));
        const activeItemCount = items
          .filter((item) => isCompletableStatus(item.status))
          .reduce((sum, item) => sum + item.quantity, 0);
        const completedItemCount = items
          .filter((item) => item.status === 'COMPLETED')
          .reduce((sum, item) => sum + item.quantity, 0);
        const cancelledItemCount = items
          .filter((item) => item.status === 'CANCELLED')
          .reduce((sum, item) => sum + item.quantity, 0);
        const totalPrice = items
          .filter((item) => item.status !== 'CANCELLED')
          .reduce((sum, item) => sum + item.price * item.quantity, 0);

        return {
          ...group,
          entries,
          items,
          displayStatus,
          latestActivityLabel,
          totalPrice,
          activeItemCount,
          completedItemCount,
          cancelledItemCount,
          representativeOrder:
            entries.find((entry) => isCompletableStatus(entry.status)) || entries[0],
        };
      })
      .sort(
        (a, b) =>
          new Date(b.latestCreatedAt).getTime()
          - new Date(a.latestCreatedAt).getTime(),
      );
  }, [orders]);

  const activeOrderCount = groupedOrders.filter((group) =>
    group.entries.some((entry: Order) => isCompletableStatus(entry.status)),
  ).length;
  const completedOrderCount = groupedOrders.filter((group) =>
    group.entries.every((entry: Order) => entry.status === 'COMPLETED'),
  ).length;
  const cancelledOrderCount = groupedOrders.filter((group) =>
    group.entries.some((entry: Order) => entry.status === 'CANCELLED'),
  ).length;
  const activeItemCount = groupedOrders.reduce(
    (sum, group) => sum + group.activeItemCount,
    0,
  );
  const completedItemCount = groupedOrders.reduce(
    (sum, group) => sum + group.completedItemCount,
    0,
  );
  const cancelledItemCount = groupedOrders.reduce(
    (sum, group) => sum + group.cancelledItemCount,
    0,
  );

  const handleFetchHistory = React.useCallback(async (group: any) => {
    if (expandedHistory[group.groupKey]) {
      setExpandedHistory((prev) => {
        const nextHistory = { ...prev };
        delete nextHistory[group.groupKey];
        return nextHistory;
      });
      return;
    }

    setLoadingHistory((prev) => ({ ...prev, [group.groupKey]: true }));

    try {
      const historiesByOrder = await getOrderHistories(
        group.entries.map((entry: Order) => entry.id),
      );

      const history = Object.values(historiesByOrder)
        .flat()
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime()
            - new Date(a.createdAt).getTime(),
        );

      setExpandedHistory((prev) => ({
        ...prev,
        [group.groupKey]: history,
      }));
    } catch (error) {
      console.error('Failed to fetch order history', error);
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [group.groupKey]: false }));
    }
  }, [expandedHistory]);

  const openMemoEditor = React.useCallback((group: any) => {
    setMemoEditorGroup(group);
    setMemoDraft(group.sellerMemo || '');
  }, []);

  const closeMemoEditor = React.useCallback(() => {
    if (isSavingMemo) {
      return;
    }

    setMemoEditorGroup(null);
    setMemoDraft('');
  }, [isSavingMemo]);

  const handleSaveMemo = React.useCallback(async () => {
    if (!memoEditorGroup) {
      return;
    }

    const memo = memoDraft.trim();

    try {
      setIsSavingMemo(true);
      const updatedOrders = await Promise.all(
        memoEditorGroup.entries.map((entry: Order) =>
          updateOrder(entry.id, {
            sellerMemo: memo,
            status: entry.status,
          }),
        ),
      );
      const updatedOrderMap = new Map(
        updatedOrders.map((order) => [order.id, order]),
      );

      setOrders((currentOrders) =>
        currentOrders.map((order) => updatedOrderMap.get(order.id) || order),
      );
      if (updatedOrders[0]) {
        onOrderMutated?.(updatedOrders[0]);
      }
      setMemoEditorGroup(null);
      setMemoDraft('');
    } catch (error) {
      console.error('Failed to save seller memo:', error);
      alert('메모 저장에 실패했습니다.');
    } finally {
      setIsSavingMemo(false);
    }
  }, [memoDraft, memoEditorGroup, onOrderMutated]);

  const openManualOrderEditor = React.useCallback((group: any) => {
    setManualOrderGroup(group);
    setManualOrderDraft({ name: '', quantity: '1', price: '' });
  }, []);

  const closeManualOrderEditor = React.useCallback(() => {
    if (isSavingManualOrder) {
      return;
    }

    setManualOrderGroup(null);
    setManualOrderDraft({ name: '', quantity: '1', price: '' });
  }, [isSavingManualOrder]);

  const handleSaveManualOrder = React.useCallback(async () => {
    if (!manualOrderGroup) {
      return;
    }

    const targetOrder =
      manualOrderGroup.entries.find((entry: Order) =>
        isCompletableStatus(entry.status),
      ) || manualOrderGroup.representativeOrder;
    const name = manualOrderDraft.name.trim();
    const quantity = Number(manualOrderDraft.quantity);
    const price = Number(manualOrderDraft.price.replace(/[^\d]/g, ''));

    if (!targetOrder || !isCompletableStatus(targetOrder.status)) {
      alert('진행 중인 주문에만 임의주문을 추가할 수 있습니다.');
      return;
    }

    if (!name) {
      alert('상품이름을 입력해주세요.');
      return;
    }

    if (!Number.isInteger(quantity) || quantity <= 0) {
      alert('수량은 1개 이상으로 입력해주세요.');
      return;
    }

    if (!Number.isInteger(price) || price < 0) {
      alert('가격을 0원 이상으로 입력해주세요.');
      return;
    }

    const nextItems = [
      ...(targetOrder.items || []),
      {
        name,
        quantity,
        price,
        image: '',
      },
    ];
    const nextTotalPrice = nextItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    const orderData = {
      items: nextItems,
      totalPrice: nextTotalPrice,
      status: targetOrder.status,
      isForced: true,
      modifierRole: 'SELLER',
    };

    try {
      setIsSavingManualOrder(true);
      const updatedOrder = await updateOrder(targetOrder.id, orderData);
      onOrderMutated?.(updatedOrder);
      setSelectedItems({});
      setManualOrderGroup(null);
      setManualOrderDraft({ name: '', quantity: '1', price: '' });
      await loadHistory();
    } catch (error: any) {
      console.error('Failed to add manual order item:', error);
      alert(error?.response?.data?.message || '임의주문 추가에 실패했습니다.');
    } finally {
      setIsSavingManualOrder(false);
    }
  }, [loadHistory, manualOrderDraft, manualOrderGroup, onOrderMutated]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl bg-white rounded-[32px] flex flex-col max-h-[90vh] shadow-2xl animate-in zoom-in duration-200">
        <div className="p-6 border-b flex justify-between items-center shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xl font-black text-gray-900">
                {customerName} 고객 주문 이력
              </h4>
              {userId ? (
                <span className="text-[10px] bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full font-black">
                  회원
                </span>
              ) : (
                <span className="text-[10px] bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-black">
                  비회원
                </span>
              )}
            </div>
            <p className="text-sm font-bold text-gray-400 mt-1">
              {formatPhoneNumber(customerPhone)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-[24px] border border-blue-100 bg-blue-50/70 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Package size={16} className="text-blue-600" />
                <h5 className="text-sm font-black text-gray-900">진행 중 주문</h5>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 bg-white rounded-2xl px-3 py-3 border border-blue-100">
                  <div>
                    <p className="text-xs font-black text-gray-900">주문 건수</p>
                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                      아직 수령완료되지 않은 주문지
                    </p>
                  </div>
                  <div className="text-xs font-black text-blue-600 shrink-0">
                    {activeOrderCount}건
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 bg-white rounded-2xl px-3 py-3 border border-blue-100">
                  <div>
                    <p className="text-xs font-black text-gray-900">주문 수량</p>
                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                      진행 중 품목 수량 합계
                    </p>
                  </div>
                  <div className="text-xs font-black text-blue-600 shrink-0">
                    {activeItemCount}개
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-emerald-100 bg-emerald-50/70 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Check size={16} className="text-emerald-600" />
                <h5 className="text-sm font-black text-gray-900">구매 완료 주문</h5>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 bg-white rounded-2xl px-3 py-3 border border-emerald-100">
                  <div>
                    <p className="text-xs font-black text-gray-900">완료 건수</p>
                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                      수령완료 처리된 주문지
                    </p>
                  </div>
                  <div className="text-xs font-black text-emerald-600 shrink-0">
                    {completedOrderCount}건
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 bg-white rounded-2xl px-3 py-3 border border-emerald-100">
                  <div>
                    <p className="text-xs font-black text-gray-900">완료 수량</p>
                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                      구매 완료 품목 수량 합계
                    </p>
                  </div>
                  <div className="text-xs font-black text-emerald-600 shrink-0">
                    {completedItemCount}개
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-red-100 bg-red-50/70 p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle size={16} className="text-red-500" />
                <h5 className="text-sm font-black text-gray-900">취소 포함 내역</h5>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 bg-white rounded-2xl px-3 py-3 border border-red-100">
                  <div>
                    <p className="text-xs font-black text-gray-900">취소 주문지</p>
                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                      취소 이력이 포함된 주문지
                    </p>
                  </div>
                  <div className="text-xs font-black text-red-500 shrink-0">
                    {cancelledOrderCount}건
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 bg-white rounded-2xl px-3 py-3 border border-red-100">
                  <div>
                    <p className="text-xs font-black text-gray-900">취소 수량</p>
                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                      취소 또는 제외된 품목 합계
                    </p>
                  </div>
                  <div className="text-xs font-black text-red-500 shrink-0">
                    {cancelledItemCount}개
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <ClipboardList size={48} strokeWidth={1} />
              <p className="text-sm font-bold">주문 내역을 불러오는 중입니다.</p>
            </div>
          ) : loadError ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <ClipboardList size={48} strokeWidth={1} />
              <p className="text-sm font-bold">{loadError}</p>
            </div>
          ) : groupedOrders.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <ClipboardList size={48} strokeWidth={1} />
              <p className="text-sm font-bold">주문 내역이 없습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {groupedOrders.map((group) => {
                const selectableKeys = (group.items || [])
                  .filter((item: any) => isCompletableStatus(item.status))
                  .map((item: any) => getItemSelectionKey(group.groupKey, item));
                const allChecked =
                  selectableKeys.length > 0
                  && selectableKeys.every((key: string) => selectedItems[key]);
                const statusMeta = getStatusMeta(group.displayStatus);
                const editTargetOrder = group.representativeOrder;
                const sellerMemo = group.sellerMemo?.trim();
                const canAddManualOrder = group.entries.some((entry: Order) =>
                  isCompletableStatus(entry.status),
                );

                return (
                  <div
                    key={group.groupKey}
                    className="bg-white rounded-[24px] border shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                  >
                    <div className="px-6 py-4 bg-gray-50/50 flex justify-between items-center border-b shrink-0">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => {
                            const newSelected = { ...selectedItems };
                            selectableKeys.forEach((key: string) => {
                              newSelected[key] = !allChecked;
                            });
                            setSelectedItems(newSelected);
                          }}
                          disabled={selectableKeys.length === 0}
                          className="text-gray-400 hover:text-gray-900 transition-colors disabled:opacity-30"
                        >
                          {allChecked ? (
                            <CheckSquare size={20} className="text-emerald-500" />
                          ) : (
                            <Square size={20} />
                          )}
                        </button>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-black text-gray-900">
                              {group.orderSheetLabel}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[9px] font-black ${statusMeta.className}`}
                            >
                              {statusMeta.label}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-slate-100 text-slate-500">
                              주문 {group.entries.length}건 통합
                            </span>
                          </div>
                          <div className="mt-1 text-[10px] text-gray-400 font-bold flex items-center gap-1.5 flex-wrap">
                            <Calendar size={12} />
                            최근 주문 {group.latestActivityLabel || '-'}
                            <span className="text-gray-200">|</span>
                            <Package size={12} />
                            {group.storeName}
                            {group.deliveryDate ? (
                              <>
                                <span className="text-gray-200">|</span>
                                배송일{' '}
                                {new Date(group.deliveryDate).toLocaleDateString(
                                  'ko-KR',
                                  {
                                    month: 'numeric',
                                    day: 'numeric',
                                    weekday: 'short',
                                  },
                                )}
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {onUpdateOrderStatus && group.manageableIds.length > 0 && (
                        <select
                          value={group.displayStatus === 'PARTIAL' ? 'PENDING' : group.displayStatus}
                          onChange={async (e) => {
                            const nextStatus = e.target.value as OrderStatus;
                            if (onUpdateOrderStatusBatch) {
                              await onUpdateOrderStatusBatch(group.manageableIds, nextStatus);
                              await loadHistory();
                              return;
                            }

                            if (editTargetOrder) {
                              await onUpdateOrderStatus({
                                ...editTargetOrder,
                                status: nextStatus,
                              });
                              await loadHistory();
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-black border transition-colors outline-none cursor-pointer appearance-none ${statusMeta.className}`}
                        >
                          <option value="PENDING">주문완료</option>
                          <option value="READY">상품준비완료</option>
                          <option value="COMPLETED">입금/수령완료</option>
                          <option value="CANCELLED">취소됨</option>
                        </select>
                      )}
                    </div>

                    <div className="p-6 space-y-5">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                            주문지 통합 품목
                          </p>
                          <div className="text-[10px] font-bold text-gray-400">
                            진행 {group.activeItemCount}개 / 완료 {group.completedItemCount}개 / 취소 {group.cancelledItemCount}개
                          </div>
                        </div>
                        {(group.items || []).map((item: any, idx: number) => {
                          const itemKey = getItemSelectionKey(group.groupKey, item);
                          const isChecked = !!selectedItems[itemKey];
                          const optionLabel = getOrderItemOptionLabel(item);
                          const imageUrl = sanitizeImageUrl(item.image);
                          const itemStatusMeta = getStatusMeta(item.status);
                          const isSelectable = isCompletableStatus(item.status);
                          const isCancelled = item.status === 'CANCELLED';

                          return (
                            <div
                              key={`${itemKey}-${idx}`}
                              className={`flex items-center gap-4 rounded-2xl border p-3 ${isCancelled ? 'bg-red-50/50 border-red-100' : 'bg-white border-gray-100'}`}
                            >
                              {isSelectable ? (
                                <button
                                  onClick={() =>
                                    setSelectedItems((prev) => ({
                                      ...prev,
                                      [itemKey]: !prev[itemKey],
                                    }))
                                  }
                                  className="transition-colors"
                                >
                                  {isChecked ? (
                                    <CheckSquare size={18} className="text-emerald-500" />
                                  ) : (
                                    <Square size={18} className="text-gray-300" />
                                  )}
                                </button>
                              ) : (
                                <div className="w-[18px] shrink-0 flex items-center justify-center text-gray-200">
                                  <Square size={18} />
                                </div>
                              )}

                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  className={`w-10 h-10 rounded-xl object-cover border bg-gray-50 shrink-0 ${isCancelled ? 'grayscale opacity-70' : ''}`}
                                  alt={getOrderItemBaseName(item)}
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-xl border bg-gray-50 shrink-0 flex items-center justify-center text-gray-300">
                                  <Package size={14} />
                                </div>
                              )}

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p
                                    className={`text-xs font-black truncate ${isCancelled ? 'text-red-500 line-through' : 'text-gray-900'}`}
                                  >
                                    {getOrderItemBaseName(item)}
                                  </p>
                                  <span
                                    className={`px-1.5 py-0.5 rounded-md text-[8px] font-black shrink-0 ${itemStatusMeta.className}`}
                                  >
                                    {itemStatusMeta.label}
                                  </span>
                                </div>
                                {optionLabel && (
                                  <p
                                    className={`text-[10px] font-black mt-0.5 truncate ${isCancelled ? 'text-red-400' : 'text-emerald-600'}`}
                                  >
                                    옵션: {optionLabel}
                                  </p>
                                )}
                                <p className="text-[10px] font-bold text-gray-400 mt-0.5">
                                  {item.quantity}개 / {item.price.toLocaleString()}원
                                </p>
                              </div>
                              <div className="text-xs font-black text-gray-900 shrink-0">
                                {(item.price * item.quantity).toLocaleString()}원
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {sellerMemo && (
                        <div className="rounded-2xl bg-sky-50 border border-sky-100 px-4 py-3">
                          <p className="text-[10px] font-black text-sky-600 uppercase tracking-[0.18em]">
                            판매자 메모
                          </p>
                          <p className="mt-1.5 text-xs font-bold text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {sellerMemo}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="px-6 py-4 bg-gray-50/30 border-t flex justify-between items-center">
                      <button
                        onClick={() => handleFetchHistory(group)}
                        className="flex items-center gap-1.5 text-[11px] font-black text-gray-400 hover:text-gray-900 transition-colors"
                      >
                        <History size={12} />
                        {expandedHistory[group.groupKey]
                          ? '진행 이력 닫기'
                          : '주문지 진행 이력'}
                      </button>
                      <div className="text-base font-black text-gray-900">
                        {group.totalPrice.toLocaleString()}원
                      </div>
                    </div>

                    {expandedHistory[group.groupKey] && (
                      <div className="px-6 pb-4 border-t border-gray-50">
                        <OrderEditTimeline
                          logs={expandedHistory[group.groupKey]}
                        />
                      </div>
                    )}

                    {loadingHistory[group.groupKey] && (
                      <div className="px-6 pb-4 border-t border-gray-50">
                        <div className="mt-4 p-4 text-center text-[11px] text-gray-400 italic">
                          이력 불러오는 중...
                        </div>
                      </div>
                    )}

                    {editTargetOrder && (
                      <div className="px-6 py-3 border-t flex justify-end gap-2">
                        {canAddManualOrder && (
                          <button
                            onClick={() => openManualOrderEditor(group)}
                            className="text-[11px] font-bold text-amber-600 hover:text-amber-700 bg-amber-50 px-4 py-2 rounded-xl transition-colors"
                          >
                            임의주문 추가
                          </button>
                        )}
                        <button
                          onClick={() => openMemoEditor(group)}
                          className="text-[11px] font-bold text-sky-600 hover:text-sky-700 bg-sky-50 px-4 py-2 rounded-xl transition-colors"
                        >
                          {sellerMemo ? '메모 수정' : '메모 추가'}
                        </button>
                        {onOpenAdminOrder && (
                          <button
                            onClick={() => onOpenAdminOrder(editTargetOrder)}
                            className="text-[11px] font-bold text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-4 py-2 rounded-xl transition-colors"
                          >
                            주문 수정
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!isLoading && Object.values(selectedItems).some((value) => value) && (
          <div className="absolute bottom-6 left-6 right-6 z-10 flex flex-col items-center gap-3 animate-in slide-in-from-bottom-4 duration-300">
            <button
              onClick={() => setIsConfirmOpen(true)}
              className="bg-blue-600 text-white w-full h-14 rounded-2xl font-black text-sm shadow-xl shadow-blue-500/20 hover:bg-blue-700 transition-all flex items-center justify-center gap-2 group active:scale-[0.98]"
            >
              <span>
                입금/수령완료 처리 (
                {Object.values(selectedItems).filter((value) => value).length}
                개 품목)
              </span>
            </button>
          </div>
        )}

        {memoEditorGroup && (
          <div className="fixed inset-0 z-[8200] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={closeMemoEditor}
            />
            <div className="relative w-full max-w-md bg-white rounded-[28px] overflow-hidden shadow-2xl animate-in zoom-in duration-200">
              <div className="p-5 border-b bg-gray-50/70 flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-lg font-black text-gray-900">
                    주문지 메모
                  </h4>
                  <p className="mt-1 text-[11px] font-bold text-gray-400">
                    {memoEditorGroup.orderSheetLabel}
                  </p>
                </div>
                <button
                  onClick={closeMemoEditor}
                  disabled={isSavingMemo}
                  className="p-2 bg-white rounded-full text-gray-400 border border-gray-100 shadow-sm hover:text-gray-900 transition-colors disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <textarea
                  value={memoDraft}
                  onChange={(event) => setMemoDraft(event.target.value)}
                  className="w-full min-h-[132px] resize-none rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-bold text-gray-800 outline-none placeholder-gray-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
                  placeholder="포장, 전달, 현장 배부 참고 메모를 입력하세요"
                  maxLength={500}
                />
                <div className="flex justify-between items-center text-[10px] font-bold text-gray-400">
                  <span>비워서 저장하면 메모가 삭제됩니다.</span>
                  <span>{memoDraft.length}/500</span>
                </div>
              </div>
              <div className="p-5 bg-gray-50 border-t flex justify-end gap-2">
                <button
                  onClick={closeMemoEditor}
                  disabled={isSavingMemo}
                  className="px-4 h-11 rounded-2xl bg-white border border-gray-200 text-gray-400 text-xs font-black hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={handleSaveMemo}
                  disabled={isSavingMemo}
                  className="px-5 h-11 rounded-2xl bg-sky-600 text-white text-xs font-black shadow-lg shadow-sky-500/20 hover:bg-sky-700 transition-colors disabled:bg-gray-300 disabled:shadow-none"
                >
                  {isSavingMemo ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        )}

        {manualOrderGroup && (
          <div className="fixed inset-0 z-[8200] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={closeManualOrderEditor}
            />
            <div className="relative w-full max-w-md bg-white rounded-[28px] overflow-hidden shadow-2xl animate-in zoom-in duration-200">
              <div className="p-5 border-b bg-gray-50/70 flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-lg font-black text-gray-900">
                    임의주문 추가
                  </h4>
                  <p className="mt-1 text-[11px] font-bold text-gray-400">
                    {manualOrderGroup.orderSheetLabel}
                  </p>
                </div>
                <button
                  onClick={closeManualOrderEditor}
                  disabled={isSavingManualOrder}
                  className="p-2 bg-white rounded-full text-gray-400 border border-gray-100 shadow-sm hover:text-gray-900 transition-colors disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-500">
                    상품이름
                  </label>
                  <input
                    value={manualOrderDraft.name}
                    onChange={(event) =>
                      setManualOrderDraft((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    className="w-full h-11 bg-gray-50 border border-gray-100 rounded-xl px-3 text-sm font-bold text-gray-900 placeholder-gray-300 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all"
                    placeholder="예: 현장 추가상품"
                    maxLength={80}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-500">
                      수량
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={manualOrderDraft.quantity}
                      onChange={(event) =>
                        setManualOrderDraft((prev) => ({
                          ...prev,
                          quantity: event.target.value,
                        }))
                      }
                      className="w-full h-11 bg-gray-50 border border-gray-100 rounded-xl px-3 text-sm font-bold text-gray-900 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-500">
                      가격
                    </label>
                    <input
                      inputMode="numeric"
                      value={manualOrderDraft.price}
                      onChange={(event) =>
                        setManualOrderDraft((prev) => ({
                          ...prev,
                          price: event.target.value.replace(/[^\d]/g, ''),
                        }))
                      }
                      className="w-full h-11 bg-gray-50 border border-gray-100 rounded-xl px-3 text-sm font-bold text-gray-900 placeholder-gray-300 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] font-black text-amber-700">
                    추가 금액
                  </span>
                  <span className="text-base font-black text-gray-900">
                    {(
                      (Number(manualOrderDraft.quantity) || 0)
                      * (Number(manualOrderDraft.price.replace(/[^\d]/g, '')) || 0)
                    ).toLocaleString()}
                    원
                  </span>
                </div>
              </div>

              <div className="p-5 bg-gray-50 border-t flex justify-end gap-2">
                <button
                  onClick={closeManualOrderEditor}
                  disabled={isSavingManualOrder}
                  className="px-4 h-11 rounded-2xl bg-white border border-gray-200 text-gray-400 text-xs font-black hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={handleSaveManualOrder}
                  disabled={isSavingManualOrder}
                  className="px-5 h-11 rounded-2xl bg-amber-500 text-white text-xs font-black shadow-lg shadow-amber-500/20 hover:bg-amber-600 transition-colors disabled:bg-gray-300 disabled:shadow-none"
                >
                  {isSavingManualOrder ? '추가 중...' : '추가'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ReceiptConfirmationPopup
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          selectedItems={(() => {
            const items: any[] = [];
            groupedOrders.forEach((group) => {
              group.items.forEach((item: any) => {
                if (selectedItems[getItemSelectionKey(group.groupKey, item)]) {
                  items.push(item);
                }
              });
            });
            return items;
          })()}
          totalAmount={(() => {
            let total = 0;
            groupedOrders.forEach((group) => {
              group.items.forEach((item: any) => {
                if (selectedItems[getItemSelectionKey(group.groupKey, item)]) {
                  total += item.price * item.quantity;
                }
              });
            });
            return total;
          })()}
          onConfirm={async () => {
            const partialUpdatesMap: Record<string, string[]> = {};

            groupedOrders.forEach((group) => {
              group.items.forEach((item: any) => {
                const itemKey = getItemSelectionKey(group.groupKey, item);
                if (!selectedItems[itemKey]) {
                  return;
                }

                (item.sourceEntries || []).forEach(
                  (entry: { orderId: string; itemId: string }) => {
                    const { orderId, itemId } = entry;
                    if (!partialUpdatesMap[orderId]) {
                      partialUpdatesMap[orderId] = [];
                    }
                    if (!partialUpdatesMap[orderId].includes(itemId)) {
                      partialUpdatesMap[orderId].push(itemId);
                    }
                  },
                );
              });
            });

            const partialUpdates = Object.entries(partialUpdatesMap)
              .filter(([, itemIds]) => itemIds.length > 0)
              .map(([id, itemIds]) => ({ id, itemIds }));

            if (onUpdateOrderStatusBatch && partialUpdates.length > 0) {
              try {
                await onUpdateOrderStatusBatch(
                  [],
                  'COMPLETED',
                  undefined,
                  partialUpdates,
                );
                await loadHistory();
                setSelectedItems({});
                setIsConfirmOpen(false);
              } catch (error) {
                console.error('Failed to update orders:', error);
                alert('상태 변경 중 오류가 발생했습니다.');
              }
            }
          }}
        />
      </div>
    </div>
  );
};
